import { useZero } from "@rocicorp/zero/react";
import { getInitialConnectorValues } from "../../../shared/connectors/getInitialConnectorValues";
import { SELF_FEDERATION_FLAG } from "../../../shared/featureFlagNames";
import { sharedMutators } from "../../../shared/mutators";
import { forgetConnectorDestinationProof } from "../../sealSecret";
import {
  type ConnectorConfig,
  connectorVariants,
} from "../../lib/connectorDirectory";
import { connectorAuthTypeLabel } from "../../lib/connector";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { DropdownOpener } from "../Settings/shared";
import { Text } from "../Text";
import { InstructionsStep, StepHeading } from "./InstructionsStep";
import { useRenderWithPlaceholders } from "./renderWithPlaceholders";

// Flag-gated variants: hidden from the connection-type picker unless the
// tenant has the flag on. The variant's runtime path stays live regardless, so
// this only controls discoverability during rollout.
const FLAG_GATED_VARIANTS: Record<string, string> = {
  threeBFederated: SELF_FEDERATION_FLAG,
};

// Drop a flag-gated variant unless its flag is on, but never hide the variant
// already selected — a tenant mid-configuration keeps seeing its own choice.
export function visibleVariants(
  variants: ConnectorConfig[],
  selectedIdentifier: string,
  flagState: Record<string, boolean | undefined>
): ConnectorConfig[] {
  return variants.filter((variant) => {
    const flag = FLAG_GATED_VARIANTS[variant.identifier];
    if (!flag || variant.identifier === selectedIdentifier) return true;
    return flagState[flag] === true;
  });
}

type Props = {
  appId: string;
  tenantId: string;
  appConfig: ConnectorConfig;
};

export function VariantPicker({ appId, tenantId, appConfig }: Props) {
  const z = useZero();
  const renderWithPlaceholders = useRenderWithPlaceholders();
  const selfFederationOn = useFeatureFlag(SELF_FEDERATION_FLAG);
  const variants = visibleVariants(
    connectorVariants(appConfig),
    appConfig.identifier,
    { [SELF_FEDERATION_FLAG]: selfFederationOn }
  );

  // A variant is a different definition with its own credential fields, so
  // switching resets the draft to that definition's initial values.
  const switchTo = (variant: ConnectorConfig) => {
    if (variant.identifier === appConfig.identifier) return;
    forgetConnectorDestinationProof(tenantId, appId);
    const initialValues = getInitialConnectorValues(
      variant.definition,
      renderWithPlaceholders
    );
    z.mutate(
      sharedMutators.switchConnectorVariant({
        connectedAppId: appId,
        identifier: variant.identifier,
        name: initialValues.name,
        urlPattern: initialValues.urlPattern,
        configuration: initialValues.configuration,
        createOAuthConnection: initialValues.createOAuthConnection,
      })
    );
  };

  // With a gated variant filtered out there may be nothing to pick between, so
  // don't show a single-option picker (the parent's guard counts unfiltered
  // variants and can't see the flag).
  if (variants.length < 2) return null;

  return (
    <InstructionsStep style={{ marginBottom: 24 }}>
      <StepHeading>
        <Text variant="text-regular-md">Connection type</Text>
      </StepHeading>
      <Dropdown
        opener={
          <DropdownOpener type="button">
            <Text variant="text-regular">
              {connectorAuthTypeLabel(appConfig)}
            </Text>
            <span style={{ opacity: 0.3 }}>
              <Icon name="chevron-down" size={12} />
            </span>
          </DropdownOpener>
        }
        align="bottom-left"
        sections={[
          {
            items: variants.map((variant) => ({
              type: "button" as const,
              left: null,
              label: connectorAuthTypeLabel(variant),
              right:
                variant.identifier === appConfig.identifier ? (
                  <Icon name="check" size={12} color="var(--text-secondary)" />
                ) : undefined,
              onClick: () => switchTo(variant),
            })),
          },
        ]}
      />
    </InstructionsStep>
  );
}
